import { Pipe, PipeTransform } from '@angular/core';
import { Booking, newGuideInterface } from './models';
import { EventInput } from '@fullcalendar/core/index.js';
import { sortGuides } from './booking-from.utils';
import { isTransparent } from './color.utils';

@Pipe({
  name: 'bookingsToEvents',
  standalone: true,
})
export class BookingsToEventsPipe implements PipeTransform {
  transform(bookings: Booking[] | null | undefined): EventInput[] {
    if (!bookings || !Array.isArray(bookings)) {
      return [];
    }

    return bookings
      .filter((booking) => !booking.deleteFlag)
      .map((booking) => {
        const { date, startTime, endTime } = booking.duration; // Extract duration properties
        // Convert Firestore Timestamps to JavaScript Date objects
        const bookingDate = new Date(date.seconds * 1000);
        const startDateTime = new Date(startTime.seconds * 1000);
        const endDateTime = new Date(endTime.seconds * 1000);
        // Format the date in YYYY-MM-DD format
        const formattedDate = bookingDate.toISOString().split('T')[0];

        // Extract time in HH:MM:SS format
        const startTimeStr = startDateTime
          .toISOString()
          .split('T')[1]
          .slice(0, 8);
        const endTimeStr = endDateTime.toISOString().split('T')[1].slice(0, 8);
        const titleHtml = this.createTitleHtml(booking);

        let backgroundColor = '#007edf';

        const guides = Object.entries(booking.guides ?? {});
        if (guides.length > 0) {
          const [firstGuidId, firstGuide] = guides[0];
          if (firstGuide.tour.tourInfo.colorCode) {
            const colorCode = firstGuide.tour.tourInfo.colorCode;
            if (!isTransparent(colorCode)) {
              backgroundColor = colorCode;
            }
          }
        }

        const event = {
          id: booking.id,

          backgroundColor: backgroundColor, // Use ship color (you can customize this)
          title: `${titleHtml}`,
          start: `${formattedDate}T${startTimeStr}`, // Correctly formatted start time
          end: `${formattedDate}T${endTimeStr}`, // Correctly formatted end time
          extendedProps: {
            booking: booking,
          },
        };
        //      console.log(event);
        return event;
      });
  }

  private createTitleHtml(booking: Booking): string {
    // Check if guides are available in the booking
    if (!booking.guides) {
      return '';
    }

    // Initialize an array to hold the guide information
    const guideInfoArray: string[] = [];

    let displayGuides: newGuideInterface[] = [];
    if (booking.guides && booking.guideOrder) {
      displayGuides = sortGuides(booking.guides, booking.guideOrder);
    } else if (booking.guides) {
      //displayGuides = sortGuides(booking.guides);
      Object.entries(booking.guides).forEach(([id, guide]) => {
        displayGuides.push(guide);
      });
    }

    let tourSet = new Set<string>();
    // Iterate over the guides and extract their surname and tourInfo
    displayGuides.forEach((guide) => {
      if (guide && guide.tour && !guide.storno.isActive) {
        let guideInfo = '';
        if (guide.tour.tourConfirm) {
          guideInfo = `<span class="whitespace-nowrap"><span class="text-xs">✅ - ${guide.guideDetails.surname}</span><span class="truncate">- ${guide.positionDetails.location}</span></span>`;
        } else if (guide.storno.isActive) {
          guideInfo = `<span class="whitespace-nowrap"><span class="text-xs">❌ - ${guide.guideDetails.surname}</span><span class="truncate">- ${guide.positionDetails.location}</span></span>`;
        } else {
          guideInfo = `<span class="whitespace-nowrap"><span class="text-xs">🟡 - ${guide.guideDetails.surname}</span><span class="truncate">- ${guide.positionDetails.location}</span></span>`;
        }

        //Legacy support for the old and new values
        tourSet.add(
          typeof guide.tour.tourInfo === 'string'
            ? guide.tour.tourInfo
            : guide.tour.tourInfo.tourName,
        );

        //guideInfo += `<span class="text-xs">${guide.tour.tourInfo}</span>`;
        guideInfoArray.push(guideInfo);
      }
    });
    // Ensure guide and tourInfo are available

    tourSet.forEach((tour) => {
      guideInfoArray.push(
        `<span class="text-xs font-semibold truncate">Tour: ${tour}</span>`,
      );
    });

    // Join all guide info into a single string with line breaks
    return guideInfoArray.join('<br>');
  }
}
